import { compact, uniq } from 'lodash';
import Immutable from 'immutable';

import { sendAmplitudeEvent } from './amplitudeService';
import { EVENT_TYPE_NAMES } from '../../common/analytics/amplitudeEventTypesUtil';
import { AMPLITUDE_USER_PROPS, TRACKED_FEATURES } from '../../common/analytics/statsConstants';
import { ELEMENT_UPDATE, ELEMENT_UPDATE_TYPE } from '../../common/elements/elementConstants';
import { getElement } from '../../common/elements/utils/elementTraversalUtils';
import { getElementType } from '../../common/elements/utils/elementPropertyUtils';
import { LABEL_CREATE, LABEL_DELETE, LABEL_UPDATE } from '../../common/labels/labelConstants';
import { Label } from '../../common/labels/labelTypes';
import { ReduxState } from '../types/reduxTypes';
import { selectLabelsByIds } from '../element/labels/labelSelectors';
import { getElements } from '../element/selectors/elementsSelector';

enum LabelUpdateTypes {
    RENAME = 'rename',
    CHANGE_COLOR = 'change color',
    DELETE = 'delete',
}

type ActionWithUser = { type: string; user?: { _id?: string } };

type LabelCreateAction = ActionWithUser & { type: typeof LABEL_CREATE; label?: Label };
type LabelUpdateAction = ActionWithUser & { type: typeof LABEL_UPDATE; labelId: string; changes?: Partial<Label> };
type LabelDeleteAction = ActionWithUser & { type: typeof LABEL_DELETE; labelId: string };

type ElementLabelUpdate = { id: string; changes?: unknown; meta?: { addedLabelIds?: string[] } };
type ElementUpdateAction = ActionWithUser & {
    type: typeof ELEMENT_UPDATE;
    updateType?: string;
    updates?: ElementLabelUpdate[];
    analytics?: {
        usedSearch?: boolean;
    };
};

type LabelAnalyticsAction = LabelCreateAction | LabelUpdateAction | LabelDeleteAction | ElementUpdateAction;

type LabelUpdatedEventProperties = { updateType: LabelUpdateTypes; isLabelCreator: boolean };
type LabelAppliedEventProperties = {
    elementTypes: string[];
    elementCount: number;
    usedSearch: boolean;
    isLabelCreator: boolean;
};
type LabelAmplitudeEvent = {
    eventType: string;
    eventProperties?: LabelAppliedEventProperties | LabelUpdatedEventProperties;
};

const labelsFeatureUserProperties = {
    [AMPLITUDE_USER_PROPS.FEATURE]: { [TRACKED_FEATURES.LABELS]: true },
};

const labelElementUpdateTypes = [ELEMENT_UPDATE_TYPE.LABEL, ELEMENT_UPDATE_TYPE.TOGGLE_LABEL];

const isLabelCreator = (state: ReduxState, labelId: string, currentUserId?: string): boolean => {
    const label = selectLabelsByIds(Immutable.List([labelId]))(state)?.first();

    return label?.meta?.creator === currentUserId;
};

const sendLabelAmplitudeEvent = ({ eventType, eventProperties }: LabelAmplitudeEvent): void => {
    sendAmplitudeEvent({
        eventType,
        eventProperties,
        userProperties: labelsFeatureUserProperties,
    });
};

const getLabelUpdateType = (changes: Partial<Label> = {}): LabelUpdateTypes | undefined => {
    if ('name' in changes) return LabelUpdateTypes.RENAME;
    if ('color' in changes) return LabelUpdateTypes.CHANGE_COLOR;
};

const trackLabelAppliedFromElementUpdate = (
    action: ElementUpdateAction,
    nextState: ReduxState,
    currentUserId?: string,
): void => {
    if (!action.updateType || !labelElementUpdateTypes.includes(action.updateType)) return;

    const addedLabelIds = uniq(compact(action.updates?.flatMap((update) => update.meta?.addedLabelIds || []) || []));
    if (addedLabelIds.length === 0) return;

    const elements = getElements(nextState);

    addedLabelIds.forEach((labelId) => {
        const updatesForLabel = action.updates?.filter((update) => update.meta?.addedLabelIds?.includes(labelId)) || [];
        const elementTypes = uniq(updatesForLabel.map((update) => getElementType(getElement(elements, update.id))));

        sendLabelAmplitudeEvent({
            eventType: EVENT_TYPE_NAMES.LABEL_APPLIED,
            eventProperties: {
                elementTypes,
                elementCount: updatesForLabel.length,
                isLabelCreator: isLabelCreator(nextState, labelId, currentUserId),
                usedSearch: !!action.analytics?.usedSearch,
            },
        });
    });
};

/**
 * Tracks label analytics from Redux actions after reducers have applied them.
 */
export const trackLabelAnalytics = (
    action: LabelAnalyticsAction,
    previousState: ReduxState,
    nextState: ReduxState,
): void => {
    const currentUserId = action.user?._id;

    switch (action.type) {
        case LABEL_CREATE:
            return sendLabelAmplitudeEvent({ eventType: EVENT_TYPE_NAMES.LABEL_CREATED });
        case ELEMENT_UPDATE:
            return trackLabelAppliedFromElementUpdate(action, nextState, currentUserId);
        case LABEL_UPDATE: {
            const updateType = getLabelUpdateType(action.changes);
            if (!updateType) return;

            return sendLabelAmplitudeEvent({
                eventType: EVENT_TYPE_NAMES.LABEL_UPDATED,
                eventProperties: {
                    updateType,
                    isLabelCreator: isLabelCreator(nextState, action.labelId, currentUserId),
                },
            });
        }
        case LABEL_DELETE:
            return sendLabelAmplitudeEvent({
                eventType: EVENT_TYPE_NAMES.LABEL_UPDATED,
                eventProperties: {
                    updateType: LabelUpdateTypes.DELETE,
                    isLabelCreator: isLabelCreator(previousState, action.labelId, currentUserId),
                },
            });
        default:
            return undefined;
    }
};
